import { isValidElement, type ReactElement, type ReactNode, type RefObject } from 'react';
import invariant from 'tiny-invariant';
import { Icon } from '../Icon';
import type { Action } from './Actions';
import {
  Button,
  MenuItem,
  MenuList,
  MenuListProps,
  MenuSeparator,
  PopoverRoot,
  PopoverTrigger,
  PopoverPopup,
  type PopoverHandle,
  ButtonProps,
} from '@ff-it/ui';

import { Placement } from '@floating-ui/react';

type ActionDropdownProps = {
  actions: (Action | '-' | ReactElement)[];
  children?: ReactNode;
  testId?: string;
  menuTestId?: string;
  className?: string;
  size?: ButtonProps['size'] | null;
  buttonRef?: RefObject<HTMLButtonElement>;
  portalId?: string;
  menuSize?: MenuListProps['size'];
  after?: ReactNode;
  popoverHandle?: PopoverHandle;
  title?: string;
} & Pick<ButtonProps, 'variant' | 'icon' | 'size' | 'disabled'>;

export const allowedPlacements: Placement[] = ['bottom-start', 'bottom-end', 'top-start', 'top-end'];

type ActionMenuProps = Pick<ActionDropdownProps, 'actions' | 'testId' | 'menuSize' | 'after'>;

export function ActionMenu({ actions, testId, menuSize, after }: ActionMenuProps): ReactElement {
  return (
    <MenuList testId={testId ? `${testId}-menu` : undefined} size={menuSize}>
      {actions.map((item, idx) => {
        if (item === '-') {
          return <MenuSeparator key={`separator-${idx}`} />;
        }
        if (isValidElement(item)) {
          invariant(item.key != null, 'ActionDropdown: ReactElement items must have an explicit key');
          return item;
        }

        const { action, button: { icon, className: _, children, variant, ...button } = {} } = item as Action;

        return (
          <MenuItem
            key={action}
            testId={`action-${action}`}
            {...(button as any)}
            prefix={icon ? <Icon icon={icon} color={variant === 'danger' ? 'error500' : undefined} /> : undefined}
            label={children}
          />
        );
      })}
      {after}
    </MenuList>
  );
}

export function ActionDropdown({
  actions,
  children,
  icon,
  testId,
  className,
  variant,
  size,
  menuSize,
  buttonRef,
  after,
  popoverHandle,
  title,
  disabled,
}: ActionDropdownProps): ReactElement | null {
  if (actions.length === 0) {
    return null;
  }

  return (
    <PopoverRoot handle={popoverHandle}>
      <PopoverTrigger>
        <Button
          testId={testId}
          className={className}
          variant={variant}
          size={size}
          ref={buttonRef}
          icon={icon}
          title={title}
          disabled={disabled}
        >
          {children}
        </Button>
      </PopoverTrigger>
      <PopoverPopup closeOnContentClick placement="auto" placementOptions={{ allowedPlacements }}>
        <ActionMenu actions={actions} testId={testId} menuSize={menuSize} after={after} />
      </PopoverPopup>
    </PopoverRoot>
  );
}
